import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '@/features/auth/authStore';
import { AuthGuard } from '@/features/auth/AuthGuard';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { MagicLoginPage } from '@/features/auth/MagicLoginPage';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DashboardContent } from '@/features/dashboard/components/DashboardContent';
import IntegrationsPage from '@/features/integrations/IntegrationsPage';
import ConversationsPage from '@/features/conversations/ConversationsPage';
import LeadsPage from '@/features/leads/LeadsPage';
import { AutomationsPage } from '@/features/automations';
import { AnalyticsPage } from '@/features/analytics';
import { SettingsPage } from '@/features/settings';
import { KnowledgeBasePage } from '@/features/knowledge';
import { OnboardingPage } from '@/features/onboarding/OnboardingPage';
import { ShopifyLinkPage } from '@/features/shopify/ShopifyLinkPage';

import { ThemeProvider } from '@/providers/ThemeProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route Component — delegates to AuthGuard, which validates the token
// server-side via /api/auth/me and logs out on failure.
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  return <AuthGuard>{children}</AuthGuard>;
};

// Public Route Component: someone already signed in when they ARRIVE at /login or /register is
// sent to the dashboard. The decision is taken once, on arrival. Signing in ON the page must not
// trigger it: the page navigates by itself (new accounts go to /onboarding), and <Navigate> runs
// in an effect, after that navigate(), so it would silently replace /onboarding with /dashboard.
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthStore();
  const [signedInOnArrival] = React.useState(() => isAuthenticated());
  return signedInOnArrival ? <Navigate to="/dashboard" replace /> : <>{children}</>;
};

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <Router>
          <div className="min-h-screen bg-background">
          <Routes>
            {/*
              The public marketing landing page now lives in the standalone
              Astro zone (apps/marketing) at the root domain — see
              docs/frontend/noorbot-mono-MARKETING-ASTRO-ZONE-NOTES.md.
              "/" here just sends visitors into the app.
            */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Magic-link auto-login → deep link to a conversation */}
            <Route path="/c" element={<MagicLoginPage />} />
            <Route path="/c/:conversationId" element={<MagicLoginPage />} />

            {/* "Link your OmnibizChat account" from the app in Shopify admin. It handles sign-in
                itself, so a signed-out merchant comes back to it after logging in. */}
            <Route path="/shopify/link" element={<ShopifyLinkPage />} />

            {/* Public Routes */}
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } />

            {/* First-run setup wizard: full screen, outside the dashboard chrome. The step is a
                path segment so Meta's OAuth can return to /onboarding/channel. */}
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route path="/onboarding/:step" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DashboardContent />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/integrations" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <IntegrationsPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/conversations" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ConversationsPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/leads" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <LeadsPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/automations" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AutomationsPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AnalyticsPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SettingsPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/knowledge" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <KnowledgeBasePage />
                </DashboardLayout>
              </ProtectedRoute>
            } />

            {/* Fallback for unknown paths */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
        
        {/* Toast notifications */}
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#333',
              border: '1px solid #e2e8f0',
            },
          }}
        />
      </Router>
    </QueryClientProvider>
  </ThemeProvider>
  );
}

export default App;
